import { type ExtractValues } from '@craft/lib';

import {
  type QuantitativeSize,
  type QuantitativeSizeString,
  type SizeUnit,
  SizeUnits,
  type UnitlessSize,
} from '../types/sizes';

export const isSizeUnit = (value: unknown): value is SizeUnit =>
  SizeUnits.includes(value as SizeUnit);

const QuantitativeSizeRegex = new RegExp(`^([0-9]*(.)?[0-9]+)(${SizeUnits.join('|')})$`);

const UnitlessSizeRegex = /^(-)?([0-9]*(\.)?[0-9]+)$/;

const numericSizeIsValid = (size: number) => !isNaN(size) && isFinite(size);

type InferQuantitativeSizeValue<T extends number | string> =
  T extends `${infer N extends number}${SizeUnit}`
    ? N
    : T extends `${infer N extends number}`
      ? N
      : T extends number
        ? T
        : number;

type InferQuantitativeSizeUnit<T extends number | string> = T extends number
  ? null
  : T extends `${number}`
    ? null
    : T extends `${number}${SizeUnit}`
      ? ExtractValues<{
          [key in SizeUnit]: T extends `${number}${key}` ? key : never;
        }>
      : null | SizeUnit;

type ParsedQuantitativeSize<T extends number | string, O extends { strict?: boolean }> = O extends {
  strict: false;
}
  ? [InferQuantitativeSizeValue<T>, InferQuantitativeSizeUnit<T>] | [null, null]
  : [InferQuantitativeSizeValue<T>, InferQuantitativeSizeUnit<T>];

const parseQuantitativeSizeString = <T extends string, O extends { strict?: boolean }>(
  value: T,
  opts?: O,
): ParsedQuantitativeSize<T, O> => {
  /* Both of the regexes for a full quantitative size (i.e. "30px") and a unitless size (i.e. "30")
     will be tested.  They are mutually exclusive, so if one properly parses it indicates that the
     value is of that form. */
  const stringUnitlessExec = UnitlessSizeRegex.exec(value);
  const quantitativeSizeExec = QuantitativeSizeRegex.exec(value);

  if (stringUnitlessExec?.length === 4) {
    const unitless = parseFloat(value);
    if (!numericSizeIsValid(unitless)) {
      /* If the unitless size is not a valid numeric size, an Error should be thrown regardless of
         the 'strict' flag.  This is because it would be unexpected, and likely indicative of an
         error in the implementation logic rather than an error from an invalid 'value'. */
      throw new Error(
        'Unexpected Condition: The regex yielded an invalid numeric size even though the regex ' +
          'definition should only result in valid numbers.  This most likely means the regex ' +
          'itself has changed, or is incorrect.',
      );
    }
    return [unitless, null] as ParsedQuantitativeSize<T, O>;
  } else if (quantitativeSizeExec?.length === 4) {
    const [_, size, __, unit] = quantitativeSizeExec;
    const numericSize = parseFloat(size);
    /* If any of these two conditions are met, an Error should be thrown regardless of the 'strict'
       flag.  This is because they are unexpected, and indicates the logic in this file is incorrect
       but not necessarily that the provided 'value' is incorrect. */
    if (!numericSizeIsValid(numericSize)) {
      throw new Error(
        'Unexpected Condition: The regex yielded an invalid numeric size even though the regex ' +
          'definition should only result in valid numbers.  This most likely means the regex ' +
          'itself has changed, or is incorrect.',
      );
    } else if (!isSizeUnit(unit)) {
      throw new Error(
        'Unexpected Condition: The regex yielded an invalid size unit even though the regex ' +
          'definition should only result in valid units.  This most likely means the regex ' +
          'itself has changed, or is incorrect.',
      );
    }
    return [numericSize, unit] as ParsedQuantitativeSize<T, O>;
  } else if (opts?.strict === false) {
    return [null, null] as ParsedQuantitativeSize<T, O>;
  }
  throw new TypeError(`The provided size, '${value}', is invalid!`);
};

interface ParseQuantitativeFontSizeOptions {
  readonly errorMessage?: string;
  readonly strict?: boolean;
}

/**
 * Parses the provided size value, {@link T}, into it's corresponding size value, {@link number},
 * and size unit, {@link SizeUnit}, if applicable.
 *
 * If the provided value is invalid, an {@link TypeError} will be thrown, unless the 'strict' option
 * is explicitly 'false'.
 *
 * @template T The value being parsed.
 * @template O The options for the parser.
 */
export const parseQuantitativeSize = <
  T extends number | string,
  O extends ParseQuantitativeFontSizeOptions,
>(
  value: T,
  opts?: O,
): ParsedQuantitativeSize<T, O> => {
  if (typeof value === 'string') {
    return parseQuantitativeSizeString(value, opts);
  } else if (!numericSizeIsValid(value)) {
    if (opts?.strict === false) {
      return [null, null] as ParsedQuantitativeSize<T, O>;
    }
    throw new TypeError(opts?.errorMessage ?? `The provided size, '${value}', is invalid!`);
  }
  return [value, null] as ParsedQuantitativeSize<T, O>;
};

/**
 * A typeguard that returns whether or not the provided value is a valid {@link QuantitativeSize}
 * with the specified unit, {@link U}.
 *
 * @param {number | string} value
 *   The value that the typeguard should determine whether or not it is a valid
 *   {@link QuantitativeSize} with the specified unit, {@link U}.
 * @param {U} unit
 *   The {@link SizeUnit} for which the typeguard should return true in the case that the value is
 *   a size with that unit.
 *
 * @returns {boolean}
 */
export const isQuantitativeSizeOfUnit = <U extends SizeUnit>(
  value: number | string,
  unit: U,
): value is `${number}${U}` => {
  const [_, parsedUnit] = parseQuantitativeSize(value, { strict: false });
  return parsedUnit === unit;
};

export type QuantitativeSizeToString<
  T extends QuantitativeSize,
  U extends SizeUnit,
> = T extends UnitlessSize
  ? QuantitativeSizeString<U, T>
  : T extends QuantitativeSizeString
    ? T
    : never;

export const quantitativeSizeToString = <T extends QuantitativeSize, U extends SizeUnit>(
  size: T,
  unit: U,
): QuantitativeSizeToString<T, U> => {
  const [value, implicitUnit] = parseQuantitativeSize(size);
  return `${value}${implicitUnit ?? unit}` as QuantitativeSizeToString<T, U>;
};
